import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import apiService from '../services/api';
import {
  getDocumentLimitWarning,
  getUpgradeMessage,
  planHasFeature,
  resolveEffectivePlanTier,
  type GatedFeature,
} from '../utils/planEntitlements';
import { PLAN_UPGRADE_CONTACT_EMAIL, SUBSCRIPTION_PLANS_ENABLED } from '../constants/subscriptionPlans';
import { getCurrentUserRole } from '../utils/authRole';

interface BillingProfile {
  selectedPlanId?: string | null;
  status?: string;
  paidUntil?: string | null;
}

const COMPLIANCE_PATH_PREFIX = '/compliance';
const REPORTING_PATH_PREFIX = '/reporting';
const FINANCE_PATH_PREFIXES = ['/finance', '/incentives', '/green-loans', '/gift-schemes', '/gift-applications', '/adeetie'];

const UpgradePromptBanner: React.FC = () => {
  const location = useLocation();
  const [profile, setProfile] = useState<BillingProfile | null>(null);
  const [documentCount, setDocumentCount] = useState(0);

  useEffect(() => {
    // Billing/status is MSME-only (403 for other roles). Skip when plans are off or role is not MSME.
    if (!SUBSCRIPTION_PLANS_ENABLED || getCurrentUserRole() !== 'msme') {
      setProfile(null);
      setDocumentCount(0);
      return undefined;
    }

    let mounted = true;
    const load = async () => {
      try {
        const [billingRes, statsRes] = await Promise.all([
          apiService.getBillingStatus() as Promise<{ success?: boolean; data?: { profile?: BillingProfile } }>,
          apiService.getMSMEStats().catch(() => null) as Promise<{
            data?: { documentBulkUpload?: { totalDocuments?: number } };
          } | null>,
        ]);
        if (!mounted) {
          return;
        }
        setProfile(billingRes?.data?.profile ?? null);
        const docs = Number(statsRes?.data?.documentBulkUpload?.totalDocuments ?? 0);
        setDocumentCount(docs);
      } catch {
        if (mounted) {
          setProfile(null);
        }
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  const tier = resolveEffectivePlanTier(profile?.selectedPlanId, profile?.status, profile?.paidUntil);

  const gatedFeatureNeeded = useMemo((): GatedFeature | null => {
    const path = location.pathname;
    if (path.startsWith(COMPLIANCE_PATH_PREFIX) || path.startsWith(REPORTING_PATH_PREFIX)) {
      return 'complianceHub';
    }
    if (FINANCE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) {
      return 'greenFinance';
    }
    return null;
  }, [location.pathname]);

  const upgradeMessage =
    gatedFeatureNeeded && !planHasFeature(tier, gatedFeatureNeeded)
      ? getUpgradeMessage(gatedFeatureNeeded, tier)
      : '';

  const isFinancePath = FINANCE_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
  const financeFeatureLocked = isFinancePath && gatedFeatureNeeded === 'greenFinance' && !planHasFeature(tier, 'greenFinance');

  const docWarning = getDocumentLimitWarning(tier, documentCount);

  if (!SUBSCRIPTION_PLANS_ENABLED || (!upgradeMessage && !docWarning)) {
    return null;
  }

  return (
    <Stack spacing={1.5} sx={{ mb: 2 }}>
      {upgradeMessage ? (
        <Alert
          severity="warning"
          action={(
            <Button
              component="a"
              href={`mailto:${PLAN_UPGRADE_CONTACT_EMAIL}?subject=MSME%20plan%20upgrade`}
              color="inherit"
              size="small"
              variant="outlined"
            >
              Contact us
            </Button>
          )}
        >
          <Typography variant="body2">{upgradeMessage}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {financeFeatureLocked
              ? `Growth unlocks reward redemption and full green finance workflows. Email ${PLAN_UPGRADE_CONTACT_EMAIL} to upgrade.`
              : `Growth unlocks compliance hub, BRSR exports, green finance, and carbon credits. Email ${PLAN_UPGRADE_CONTACT_EMAIL} to upgrade.`}
          </Typography>
        </Alert>
      ) : null}
      {docWarning ? (
        <Alert
          severity={docWarning.atLimit ? 'error' : 'info'}
          action={(
            <Button
              component="a"
              href={`mailto:${PLAN_UPGRADE_CONTACT_EMAIL}?subject=Document%20limit%20upgrade`}
              color="inherit"
              size="small"
              variant="outlined"
            >
              Contact us
            </Button>
          )}
        >
          <Typography variant="body2">{docWarning.message}</Typography>
        </Alert>
      ) : null}
    </Stack>
  );
};

export default UpgradePromptBanner;
